/* 

Create a feature flag system that:
- can be turned on or off via query param
- is persistent across SPAs
- is accessible to design-system consumers

// Task 1: create API for reading setting
// Task 2: Read from query param at import time and set initial value
// Task 3: Store initial value in a cookie property
// Task 4: if no query param, read from cookie property


- if value in query param, return value from query param
- if value in cookie, return value from cookie
- return default value

// What it enables in this version: easy to test internally with the feature flag system, rolling out/back is not in scope

How it works:

my.babbel.com/dashboard => default rebrand value, not rebranded
my.babbel.com/dashboard?babbel_rebranding_2024=true => rebranded, and store rebranded in cookie property
my.babbel.com/dashboard?babbel_rebranding_2024=false => not rebranded, and store rebranded in cookie property

/**
 * @deprecated 
 * Feature flag is deprecated and will be removed in the next major version.
 * Cleanup recommendations are available in [this document](https://babbel.atlassian.net/wiki/spaces/PD/pages/502071539/Rebranding+-+web+clients+feature+flag+cleanup+recommendations)
 */
export const REBRANDING_QUERY_PARAM = 'babbel_rebranding_2024';

// We will need to differentiate between web and webview when we release the rebranding
// On standard web, we want to set the default value to true, as we want to release to end users
// On mobile, we **only** want the design-system to be rebranded **if** the mobile app asks for the rebranded version
// Unfortunately, older versions of the mobile apps aren't sending the query param, so we **must** keep false as the default value
// See this thread: https://babbel.slack.com/archives/C0485BJU1EJ/p1705651779926069 for extra context
const REBRANDED_DEFAULT_VALUE_WEB = true;
const REBRANDED_DEFAULT_VALUE_WEBVIEW = false;
// IMPORTANT NOTE: the first check used to be the common way to detected whether an app is running in a webview, but I think it's not guaranteed anymore
// Alternatively, we could force each SPA to implement a global variable `IS_WEBVIEW` that they **must** expose **before** loading the design-system so that they can implement the logic custom to their app
// For example, it could look like this:
/*

// index.js
import './setIsWebview';
import './components/App';

render(document.querySelector('#root'), App);

// setIsWebview.js
globalThis.IS_WEBVIEW = // Implement the way this specific project knows it's running in a webview

// components/App.jsx
// At the point of this import, globalThis.IS_WEBVIEW will have been set correctly, so we can use its value appropriately
import { BabbelThemeProvider } '@lessonnine/design-system.lib';


*/
const castedGlobalThis = globalThis as {
  MobileAppTracker?: object;
  webkit?: object;
  androidListener?: object;
  IS_WEBVIEW?: boolean;
};

const isRunningInWebview = Boolean(
  castedGlobalThis.MobileAppTracker ||
    castedGlobalThis.webkit ||
    castedGlobalThis.androidListener ||
    castedGlobalThis.IS_WEBVIEW
);

const REBRANDED_DEFAULT_VALUE = isRunningInWebview
  ? REBRANDED_DEFAULT_VALUE_WEBVIEW
  : REBRANDED_DEFAULT_VALUE_WEB;
const isBrowser = typeof window !== 'undefined';

export type QueryParamRebrandingValue = 'true' | 'false' | undefined;

export const stringFlagToEnum = (
  stringValue: string | null | undefined
): QueryParamRebrandingValue => {
  switch (stringValue) {
    case 'true':
      return 'true';
    case 'false':
      return 'false';
    default:
      return undefined;
  }
};

const readRebrandedQueryParam = () => {
  if (!isBrowser) {
    return undefined;
  }
  // TODO: won't work with SSR
  const originalIsRebrandedFromQueryParam = new URL(window.location.href).searchParams.get(
    REBRANDING_QUERY_PARAM
  );
  return stringFlagToEnum(originalIsRebrandedFromQueryParam);
};

const getRebrandedFromStorage = () => {
  if (!isBrowser) {
    return undefined;
  }
  const regexpMatch = new RegExp(`${REBRANDING_QUERY_PARAM}=(true|false)`).exec(document.cookie);
  const currentValue = regexpMatch?.[1];
  return stringFlagToEnum(currentValue);
};

const getCookieMainDomain = () => {
  const { hostname } = window.location;
  if (hostname === 'localhost') {
    return 'localhost';
  }
  return hostname.endsWith('.cn') ? 'babbel.cn' : 'babbel.com';
};

const storeRebrandedQueryParam = (
  currentStoredValue: QueryParamRebrandingValue,
  isRebrandedFromQueryParam: QueryParamRebrandingValue
) => {
  if (isBrowser && isRebrandedFromQueryParam !== undefined) {
    if (currentStoredValue !== isRebrandedFromQueryParam) {
      const mainDomain = getCookieMainDomain();
      const yearInSeconds = 60 * 60 * 24 * 365;
      document.cookie = `${REBRANDING_QUERY_PARAM}=${isRebrandedFromQueryParam}; Domain=${mainDomain}; Max-Age=${yearInSeconds}; Path=/; SameSite=strict; Secure`;
    }
  }
};

/**
 * @deprecated
 * Feature flag is deprecated and will be removed in the next major version.
 * Cleanup recommendations are available in [this document](https://babbel.atlassian.net/wiki/spaces/PD/pages/502071539/Rebranding+-+web+clients+feature+flag+cleanup+recommendations)
 */
export const getIsRebranded = (): boolean => {
  const isRebrandedFromQueryParam = readRebrandedQueryParam();
  const isRebrandedFromStorage = getRebrandedFromStorage();

  storeRebrandedQueryParam(isRebrandedFromStorage, isRebrandedFromQueryParam);

  if (isRebrandedFromQueryParam !== undefined) {
    return isRebrandedFromQueryParam === 'true';
  }
  if (isRebrandedFromStorage !== undefined) {
    return isRebrandedFromStorage === 'true';
  }
  return REBRANDED_DEFAULT_VALUE;
};

/**
 * @deprecated
 * Feature flag is deprecated and will be removed in the next major version.
 * Cleanup recommendations are available in [this document](https://babbel.atlassian.net/wiki/spaces/PD/pages/502071539/Rebranding+-+web+clients+feature+flag+cleanup+recommendations)
 */
const isRebranded = getIsRebranded();

export { isRebranded };
